/*******************************************************************
* PAGE        : useRestoreSession
* REMARKS     : Login state (isAuthenticated/sidebar/allowedRoutes) lives in
*               redux-persist backed by sessionStorage, which is scoped per
*               tab. Tokens (accessToken/userIdentification/loginTime) live in
*               localStorage, which is shared across tabs/windows of the same
*               browser. So opening the app in a NEW tab while already logged
*               in elsewhere still landed on the login page. This hook checks
*               localStorage for a still-valid session on mount and, if found,
*               rebuilds the redux auth state instead of asking for login again.
* CREATED ON  : 29-09-2026
* AUTHOR      : Bamroliya.Paras
********************************************************************/

import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { buildSidebarMenu } from '../components/SidebarComponent/buildSidebarMenu';
import { CMExpressAPIGetUserWiseSetupRights, CMExpressAPIMultimediaDocumentPath, CMExpressAPIUserMasterListGet } from '../services/apiController';
import localDataStorage from './localDataStorage';
import { addToMasterArray, setAllowedRoutes, setIsAuthenticated, setRefreshToken, setSidebarMenu, setUserMasterId, setUserMasterIdentification } from './reduxMasterSlice';
import { SESSION_TIMEOUT_MS } from './sessionConfig';

// Returns true while it is still checking for a restorable session (caller should hold off on
// rendering the login page/routes until this settles to avoid a flash of the login screen).
const useRestoreSession = () => {
    const dispatch = useDispatch();
    const isAuthenticated = useSelector(state => state.reduxMaster.isAuthenticated);
    const [isChecking, setIsChecking] = useState(!isAuthenticated);

    useEffect(() => {
        if (isAuthenticated) {
            setIsChecking(false);
            return;
        }

        const restoreSession = async () => {
            const accessToken = localDataStorage.getLocalStorage('accessToken');
            const userIdentification = localDataStorage.getLocalStorage('userIdentification');
            const userMasterId = localDataStorage.getLocalStorage('userMasterId');
            const loginTime = localDataStorage.getLocalStorage('loginTime');

            const isSessionValid = Boolean(accessToken && userIdentification && loginTime)
                && (new Date().getTime() - loginTime) <= SESSION_TIMEOUT_MS;

            if (!isSessionValid) {
                setIsChecking(false);
                return;
            }

            try {
                // Rebuild masterArray.UserLoginInfo (profile fields used across the app: EMail,
                // Phone1, Designation, DepartmentName, CompanyGroupId, etc.) the same way login does,
                // since only tokens/ids survive in localStorage across tabs, not the full profile.
                const userMasterResponse = await CMExpressAPIUserMasterListGet({ strUserIdentification: userIdentification });
                const userLoginInfo = userMasterResponse?.status && userMasterResponse?.data?.length
                    ? userMasterResponse.data[0]
                    : null;

                if (!userLoginInfo) {
                    setIsChecking(false);
                    return;
                }

                dispatch(addToMasterArray({ name: "UserLoginInfo", data: userLoginInfo }));

                const companyGroupId = userLoginInfo.CompanyGroupId || localDataStorage.getLocalStorage('companyGroupId');

                await CMExpressAPIMultimediaDocumentPath({
                    strDocumentId: userMasterId || userLoginInfo.UserMasterId || userLoginInfo.userMasterId,
                    strCompanyGroupId: companyGroupId,
                    strFormName: "UserMaster"
                }).then((docResponse) => {
                    if (docResponse?.status && docResponse?.data?.length) {
                        dispatch(addToMasterArray({ name: "UserProfilePic", data: docResponse.data[0] }));
                    }
                });

                const response = await CMExpressAPIGetUserWiseSetupRights({ userIdentification });

                if (!response?.length) {
                    setIsChecking(false);
                    return;
                }

                const userName = String(userIdentification).trim().toLowerCase();
                const isSuperAdmin = userName === "super" || userName === "supervisor";

                const updatedResponse = isSuperAdmin
                    ? response.map(item => ({
                        ...item,
                        IsAllowAdd: true,
                        IsAllowEdit: true,
                        IsAllowDelete: true,
                        IsAllowView: true
                    }))
                    : response;

                const allowedScreens = updatedResponse.filter(r =>
                    r.IsAllowAdd || r.IsAllowEdit || r.IsAllowDelete || r.IsAllowView
                );

                if (allowedScreens.length === 0) {
                    setIsChecking(false);
                    return;
                }

                dispatch(addToMasterArray({ name: "SidebarMENU", data: allowedScreens }));
                dispatch(setSidebarMenu(buildSidebarMenu(allowedScreens)));

                const allowedRoutes = allowedScreens
                    .filter(r => r.PageUrl)
                    .map(r => r.PageUrl.toLowerCase());

                dispatch(setAllowedRoutes(allowedRoutes));

                if (userMasterId) {
                    dispatch(setUserMasterId(userMasterId));
                }
                dispatch(setUserMasterIdentification(userIdentification));

                const refreshToken = localDataStorage.getLocalStorage('refreshToken');
                if (refreshToken) {
                    dispatch(setRefreshToken(refreshToken));
                }

                dispatch(setIsAuthenticated(true));
            } catch (error) {
                // Fall through to the login page on any failure restoring the session
            } finally {
                setIsChecking(false);
            }
        };

        restoreSession();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return isChecking;
};

export default useRestoreSession;
